import React from "react";
import { LaunchParamsContext } from "../../launchParamsContext";
import { Expense, ExpenseCategory } from "../../types";
import { EXPENSE_CATEGORIES } from "../../utils";
import { categoryName } from "./cateogryName";
import { useChart } from "./useChart";
interface PieProps {
  expenses: Expense[];
}

type ExpenseByCategory = {
  sum: number;
  expenses: Array<Expense>;
};

type Data = Record<ExpenseCategory, ExpenseByCategory>;

const constructData = (expenses: Expense[]): Data =>
  expenses
    .filter((expense) => expense.amount > 0)
    .reduce((acc, expense) => {
      const category = expense.category;
      const sum = acc[category]?.sum || 0;
      const expenses = acc[category]?.expenses || [];
      return {
        ...acc,
        [category]: {
          sum: sum + expense.amount,
          expenses: [...expenses, expense],
        },
      };
    }, {} as Data);

export default function Pie({ expenses }: PieProps) {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const lp = React.useContext(LaunchParamsContext);

  const data = React.useMemo(() => constructData(expenses), [expenses]);

  const topLevelDataSet = React.useMemo(() => {
    return EXPENSE_CATEGORIES.map((category) => ({
      name: categoryName(category),
      value: data[category]?.sum || 0,
      groupId: category,
    })).filter(({ value }) => value > 0);
  }, [data]);

  const topLevelOption = React.useMemo(
    () => ({
      dataset: [{ source: topLevelDataSet }],
      tooltip: {
        trigger: "item",
      },
      legend: {},
      series: {
        type: "pie",
        universalTransition: {
          enabled: true,
          divideShape: "split",
        },
        animationDurationUpdate: 1000,
      },
    }),
    [topLevelDataSet]
  );

  const subLevelOption = React.useCallback(
    (chart: echarts.ECharts) => ({
      series: {
        type: "pie",
        universalTransition: {
          enabled: true,
          divideShape: "clone",
        },
      },
      legend: {
        data: [],
      },
      graphic: [
        {
          type: "text",
          left: 50,
          top: 20,
          style: {
            text: "Back",
            fontSize: 18,
            fill: lp?.tgWebAppThemeParams.text_color,
          },
          onclick: () => {
            chart.setOption(topLevelOption);
          },
        },
      ],
    }),
    [topLevelOption, lp]
  );

  const chart = useChart(chartRef);

  React.useEffect(() => {
    if (!chart) {
      return;
    }
    chart.setOption(topLevelOption);
    chart.on("click", (event) => {
      const groupId = (event.data as { groupId: string })
        ?.groupId as ExpenseCategory;
      if (Object.keys(data).length === 0 || !groupId || !data[groupId]) {
        return;
      }
      chart.setOption({
        dataset: [
          {
            source: data[groupId].expenses.map((e: Expense) => ({
              name: e.description,
              value: e.amount,
            })),
          },
        ],
        ...subLevelOption(chart),
      });
    });
  }, [data, topLevelOption, subLevelOption, chart]);

  return (
    <div
      ref={chartRef}
      style={{ width: "100%", height: 400, marginTop: 100 }}
    />
  );
}
